import React, { useEffect, useMemo } from 'react';
import { Form, Formik } from 'formik';
import * as Yup from 'yup';
import moment from 'moment';
import { useIntl } from 'react-intl';
import { useLocation, useNavigate } from 'react-router-dom';

import BaseForm from '../../../../components/shared/form-controls/base-form';
import FormDateSelector from '../../../../components/shared/nextgen/form/form-date-selector';
import { FormDropdown } from '../../../../components/shared/nextgen/form/form-dropdown';
import { FormInput } from '../../../../components/shared/form-controls/form-input';
import { getAllEmployees } from '../../../../features/employees/short';
import { canSeeOtherVacations } from '../../../../features/profile/entityPermissions';
import { getVacation, updateVacation } from '../../../../features/vacation/slice';
import { Vacation } from '../../../../features/vacation/types';
import { useAppDispatch, useAppSelector } from '../../../../hooks/hooks';
import { Lx } from '../../../../i18n/consts';
import { NAVIGATION_ROUTES } from '../../../../routes/NavigationRoute';

interface EditVacationFormValues {
    employee: number;
    period: string[];
    message?: string;
    notifyAboutStart: number;
    notifyInAdvance: number;
}

const NOTIFY_ABOUT_START_TEAMMATES_ONLY = 0;
const NOTIFY_ABOUT_START_ALL_EMPLOYEES = 1;

export default function EditVacationPage() {
    const dispatch = useAppDispatch();
    const intl = useIntl();
    const navigate = useNavigate();
    const location = useLocation();
    const state = location.state as { vacationId?: number } | undefined;
    const vacationId = state?.vacationId;
    const employees = useAppSelector((selector) => selector.rEmployees);
    const vacations = useAppSelector((selector) => selector.vacations.items);
    const profile = useAppSelector((selector) => selector.profile);
    const canEditOtherEmployeesVacations = canSeeOtherVacations(profile);
    const minVacationDate = useMemo(() => moment().add(1, 'day').startOf('day'), []);

    useEffect(() => {
        if (canEditOtherEmployeesVacations && employees.length === 0) {
            dispatch(getAllEmployees());
        }
    }, [canEditOtherEmployeesVacations, dispatch, employees.length]);

    useEffect(() => {
        if (!vacationId) {
            navigate(NAVIGATION_ROUTES.VACATIONREPORT);
            return;
        }

        void dispatch(getVacation(vacationId));
    }, [dispatch, navigate, vacationId]);

    const vacation = useMemo(() => vacations.find((item) => item.id === vacationId) ?? null, [vacationId, vacations]);
    const selectedEmployee = useMemo(() => {
        if (!vacation) {
            return null;
        }

        return employees.find((employee) => employee.id === vacation.employeeId)
            ?? (vacation.employeeId === profile.id ? { id: profile.id, name: profile.name, isDefault: false } : null);
    }, [employees, profile.id, profile.name, vacation]);

    useEffect(() => {
        if (!vacation || canEditOtherEmployeesVacations || vacation.employeeId === profile.id) {
            return;
        }

        navigate(NAVIGATION_ROUTES.VACATIONREPORT, { replace: true });
    }, [canEditOtherEmployeesVacations, navigate, profile.id, vacation]);

    const initialValues = useMemo<EditVacationFormValues | null>(() => {
        if (!vacation || !selectedEmployee) {
            return null;
        }

        return {
            employee: selectedEmployee.id,
            period: [vacation.dateFrom, vacation.dateTo],
            message: vacation.comment ?? '',
            notifyAboutStart: vacation.notifyAll ? NOTIFY_ABOUT_START_ALL_EMPLOYEES : NOTIFY_ABOUT_START_TEAMMATES_ONLY,
            notifyInAdvance: vacation.notifyNDaysInAdvance,
        };
    }, [selectedEmployee, vacation]);

    const validationSchema: Yup.Schema<EditVacationFormValues> = useMemo(() => Yup.object().shape({
        employee: Yup.number()
            .required(intl.formatMessage({ id: Lx.Vacation.INVALID_EMPLOYEE_ID })),
        period: Yup.array()
            .of(Yup.string().required())
            .length(2)
            .required()
            .test(
                'vacation-date-order',
                intl.formatMessage({ id: Lx.Vacation.INVALID_DATE_FROM }),
                (value) => {
                    if (!value || value.length < 2) {
                        return false;
                    }

                    return moment(value[0], 'YYYY-MM-DD').isSameOrBefore(moment(value[1], 'YYYY-MM-DD'), 'day');
                }
            ),
        message: Yup.string().max(240).optional(),
        notifyAboutStart: Yup.number().oneOf([
            NOTIFY_ABOUT_START_TEAMMATES_ONLY,
            NOTIFY_ABOUT_START_ALL_EMPLOYEES,
        ]).required(),
        notifyInAdvance: Yup.number().oneOf([0, 1, 2, 3, 4, 5]).required(),
    }), [intl]);

    const employeeOptions = useMemo(() => {
        if (!selectedEmployee) {
            return [];
        }

        return [{
            id: selectedEmployee.id,
            name: selectedEmployee.name,
        }];
    }, [selectedEmployee]);

    const notifyAboutStartOptions = useMemo(() => ([
        {
            id: NOTIFY_ABOUT_START_TEAMMATES_ONLY,
            name: intl.formatMessage({ id: Lx.Vacation.NOTIFY_ABOUT_START_TEAMMATES_ONLY }),
        },
        {
            id: NOTIFY_ABOUT_START_ALL_EMPLOYEES,
            name: intl.formatMessage({ id: Lx.Vacation.NOTIFY_ABOUT_START_ALL_EMPLOYEES }),
        },
    ]), [intl]);

    const notifyInAdvanceOptions = useMemo(() => ([
        { id: 0, name: intl.formatMessage({ id: Lx.Vacation.NOTIFY_IN_ADVANCE_DISABLED }) },
        { id: 1, name: intl.formatMessage({ id: Lx.Vacation.NOTIFY_IN_ADVANCE_ONE_DAY }) },
        { id: 2, name: intl.formatMessage({ id: Lx.Vacation.NOTIFY_IN_ADVANCE_TWO_DAYS }) },
        { id: 3, name: intl.formatMessage({ id: Lx.Vacation.NOTIFY_IN_ADVANCE_THREE_DAYS }) },
        { id: 4, name: intl.formatMessage({ id: Lx.Vacation.NOTIFY_IN_ADVANCE_FOUR_DAYS }) },
        { id: 5, name: intl.formatMessage({ id: Lx.Vacation.NOTIFY_IN_ADVANCE_FIVE_DAYS }) },
    ]), [intl]);

    const handleSubmit = async (values: EditVacationFormValues) => {
        if (!vacation) {
            return;
        }

        const employeeId = canEditOtherEmployeesVacations
            ? values.employee
            : profile.id;

        if (!employeeId || (!canEditOtherEmployeesVacations && vacation.employeeId !== employeeId)) {
            return;
        }

        const payload: Vacation = {
            ...vacation,
            employeeId,
            dateFrom: values.period[0],
            dateTo: values.period[1],
            comment: values.message,
            notifyNDaysInAdvance: values.notifyInAdvance,
            notifyAll: values.notifyAboutStart === NOTIFY_ABOUT_START_ALL_EMPLOYEES,
        };

        await dispatch(updateVacation(payload)).unwrap();
        navigate(NAVIGATION_ROUTES.VACATIONREPORT);
    };

    if (!initialValues || !vacation || !selectedEmployee) {
        return null;
    }

    return (
        <Formik
            initialValues={initialValues}
            validationSchema={validationSchema}
            validateOnBlur={true}
            validateOnChange={true}
            enableReinitialize
            onSubmit={handleSubmit}
        >
            <Form>
                <BaseForm
                    contentGap={24}
                    formType='edit'
                    formHeader={'vacation_edit_vacation'}
                    formHeaderDefaultMessage='Edit vacation'
                    savingTitle={intl.formatMessage({ id: Lx.General.VACATION })}
                    onClose={() => navigate(NAVIGATION_ROUTES.VACATIONREPORT)}
                    onAfterSave={() => navigate(NAVIGATION_ROUTES.VACATIONREPORT)}
                >
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '24px', width: '100%', maxWidth: '520px' }}>
                        <FormDateSelector
                            label={intl.formatMessage({ id: Lx.General.DATE })}
                            name={'period'}
                            range
                            required
                            minDate={minVacationDate}
                        />
                        <FormDropdown
                            label={intl.formatMessage({ id: Lx.General.EMPLOYEE })}
                            name={'employee'}
                            items={employeeOptions}
                            initialValue={employeeOptions[0]}
                            required
                            selectProps={{ disabled: true }}
                        />
                        <FormInput
                            label={intl.formatMessage({ id: Lx.Vacation.MESSAGE })}
                            name={'message'}
                            placeholder={intl.formatMessage({ id: Lx.Vacation.MESSAGE })}
                            required={false}
                            style={{ width: '100%', maxWidth: '520px' }}
                        />
                        <FormDropdown
                            label={intl.formatMessage({ id: Lx.Vacation.NOTIFY_ABOUT_START })}
                            name={'notifyAboutStart'}
                            items={notifyAboutStartOptions}
                            initialValue={notifyAboutStartOptions.find((item) => item.id === initialValues.notifyAboutStart)}
                            required
                        />
                        <FormDropdown
                            label={intl.formatMessage({ id: Lx.Vacation.NOTIFY_IN_ADVANCE })}
                            name={'notifyInAdvance'}
                            items={notifyInAdvanceOptions}
                            initialValue={notifyInAdvanceOptions.find((item) => item.id === initialValues.notifyInAdvance)}
                            required
                        />
                    </div>
                </BaseForm>
            </Form>
        </Formik>
    );
}
